import { curry as _curry, differenceBy as _differenceBy } from 'lodash/fp'

import {
  BoundaryEventRender,
  CanvasData,
  NodeDefinitionRender,
  SequencePointsRender,
} from '@flowx/designer/modules/bpmn-editor/bpmn-canvas.types'
import {
  CurrentNodeColors,
  NodeAuditStyles,
  PendingBoundaryEventColors,
  PendingEdgeColors,
  PendingNodeColors,
  VisitedBoundaryEventColors,
  VisitedEdgeColors,
  VisitedNodeColors,
} from '@flowx/designer/modules/bpmn-editor/bpmn-graph.constants'
import {
  NodeDefinition,
  ProcessDefinition,
} from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { BoundaryEvent } from '@flowx/designer/shared/entities/boundary-event/boundary-event.types'
import { EventNodes, GatewayNodes } from '@flowx/designer/shared/entities/node.types'
import { ProcessDefinitionEntity } from '@flowx/designer/shared/entities/process-definition.entity'
import {
  ApplicationProcessData,
  NodeActionState,
  ProcessDefinitionForInstance,
  ProcessInstance,
} from '@flowx/designer/shared/entities/process-instance.types'
import { SequencePoints } from '@flowx/designer/shared/entities/sequence.dictionary'
import { TOKEN_STATE } from '@flowx/shared/interfaces'

/**
 * Computes the visited and current node sets for the instance. Linear in the
 * total number of nodesActionStates: node definitions are indexed by id once
 * (O(1) per-state lookup); "are there remaining states for this context"
 * checks come from precomputed suffix counts instead of re-slicing + filtering
 * the tail on every iteration (which was O(N²) and the dominant cost on
 * polling-loop instances); results are deduplicated by id, since the same
 * node def can be visited thousands of times in a loop and downstream
 * consumers only need the unique set.
 */
export function getCurrentAndVisitedNodes(
  definition: ProcessDefinitionForInstance,
  instanceData: ProcessInstance
): { visited: NodeDefinition[]; current: NodeDefinition[] } {
  const nodeDefinitionsById = new Map<number, NodeDefinition>()
  definition.nodes.forEach((node) => {
    if (!nodeDefinitionsById.has(node.id)) {
      nodeDefinitionsById.set(node.id, node)
    }
  })

  const context = instanceData.embedContext
  const parentContext = instanceData.prevEmbedContext || instanceData.embedContext

  const activeTokens = instanceData.tokens.filter((token) =>
    [TOKEN_STATE.ACTIVE, TOKEN_STATE.INACTIVE, TOKEN_STATE.ON_HOLD].includes(token.state)
  )

  const visitedById = new Map<number, NodeDefinition>()
  const currentById = new Map<number, NodeDefinition>()

  activeTokens.forEach((token) => {
    const states = token.nodesActionStates
    const length = states.length

    const remainingForContext = new Array<number>(length + 1).fill(0)
    const remainingForParentContext = new Array<number>(length + 1).fill(0)
    for (let i = length - 1; i >= 0; i--) {
      remainingForContext[i] = remainingForContext[i + 1] + (states[i].context === context ? 1 : 0)
      remainingForParentContext[i] =
        remainingForParentContext[i + 1] + (states[i].context === parentContext ? 1 : 0)
    }

    states.forEach((nodeActionState, i) => {
      const foundNode =
        nodeActionState.context === context
          ? nodeDefinitionsById.get(nodeActionState.nodeId)
          : undefined
      if (foundNode) {
        visitedById.set(foundNode.id, foundNode)

        const notEndEventAndGatewayParallelType =
          foundNode.type !== EventNodes.END_EVENT &&
          foundNode.type !== GatewayNodes.GATEWAY_PARALLEL

        const isEndEventOrGatewayParallelTypeAndTokenNotInactive =
          (foundNode.type === EventNodes.END_EVENT ||
            foundNode.type === GatewayNodes.GATEWAY_PARALLEL) &&
          token.state !== TOKEN_STATE.INACTIVE

        if (
          remainingForContext[i + 1] === 0 &&
          (notEndEventAndGatewayParallelType ||
            isEndEventOrGatewayParallelTypeAndTokenNotInactive) &&
          !(nodeActionState.embedNodeId && remainingForParentContext[i + 1] > 0)
        ) {
          currentById.set(foundNode.id, foundNode)
        }
      }
    })
  })

  return { visited: [...visitedById.values()], current: [...currentById.values()] }
}

export function getVisitedBoundaryEvents(
  boundaryEvents: BoundaryEvent[],
  visitedEdges: SequencePoints[]
): BoundaryEvent[] {
  return boundaryEvents.filter((boundaryEvent) =>
    visitedEdges.some((sequence) => sequence.boundaryEventId === boundaryEvent.id)
  )
}

/**
 * An edge is visited if its target node immediately follows its source node in
 * the token's nodesActionStates array. getPreviousNode is resolved once per
 * node (it depends only on the node, not the sequence) instead of once per
 * incoming sequence.
 */
export function getVisitedSequences(
  tokens: ProcessInstance['tokens'],
  visitedNodes: NodeDefinition[]
): SequencePoints[] {
  return visitedNodes.flatMap((node) => {
    const previousNode = getPreviousNode(tokens, node)
    return node.incomingSequences.filter((sequence) =>
      sequence.boundaryEventId
        ? sequence.boundaryEventId === previousNode.triggeredByBoundaryEventId
        : sequence.startNodeId === previousNode.nodeId
    )
  })
}

export function getPreviousNode(
  tokens: ProcessInstance['tokens'],
  node: NodeDefinition
): NodeActionState {
  let foundNode: NodeActionState
  tokens.some((token) => {
    const foundIndex = token.nodesActionStates.findIndex((_node) => _node.nodeId === node.id)
    if (foundIndex === 0) {
      foundNode = token.nodesActionStates[foundIndex]
    }
    if (foundIndex > 0) {
      foundNode = token.nodesActionStates[foundIndex - 1]
    }
  })
  return foundNode
}

const decorateNodeWithRenderInfo = _curry(
  (flxStylesUpdate: NodeAuditStyles, node: NodeDefinitionRender): NodeDefinitionRender => ({
    ...node,
    flxStyles: { ...node?.flxStyles, ...flxStylesUpdate },
  })
)

const decorateSequenceWithRenderInfo = _curry(
  (flxStylesUpdate: NodeAuditStyles, sequence: SequencePointsRender): SequencePointsRender => ({
    ...sequence,
    flxStyles: { ...sequence?.flxStyles, ...flxStylesUpdate },
  })
)

const decorateBoundaryEventWithRenderInfo = _curry(
  (flxStylesUpdate: NodeAuditStyles, boundaryEvent: BoundaryEventRender): BoundaryEventRender => ({
    ...boundaryEvent,
    flxStyles: { ...boundaryEvent?.flxStyles, ...flxStylesUpdate },
  })
)

/**
 * Assembles the CanvasData a process instance should render: nodes split into
 * visited / current / pending sets, each decorated with the audit-state palette
 * consumed by the RF renderer from the flxStyles field.
 */
export function buildInstanceCanvasData(
  applicationProcessData: ApplicationProcessData
): CanvasData | null {
  const processData = applicationProcessData?.processData
  if (!processData?.definition) {
    return null
  }
  const { definition, instance, swimlanes } = processData

  const { visited: visitedNodes, current: currentNodes } = getCurrentAndVisitedNodes(
    definition,
    instance
  )
  const visitedEdges = getVisitedSequences(instance.tokens, visitedNodes)
  const pendingNodes = _differenceBy('id', definition.nodes, [...visitedNodes, ...currentNodes])
  const pendingEdges = _differenceBy('id', definition.edges, visitedEdges)

  const boundaryEvents = ProcessDefinitionEntity.getBoundaryEventsFromProcess(
    definition as ProcessDefinition
  )
  const visitedBoundaryEvents = getVisitedBoundaryEvents(boundaryEvents, visitedEdges)
  const pendingBoundaryEvents = _differenceBy('id', boundaryEvents, visitedBoundaryEvents)

  const decorateVisitedNode = decorateNodeWithRenderInfo(VisitedNodeColors)
  const decorateCurrentNode = decorateNodeWithRenderInfo(CurrentNodeColors)
  const decoratePendingNode = decorateNodeWithRenderInfo(PendingNodeColors)
  const decorateVisitedEdge = decorateSequenceWithRenderInfo(VisitedEdgeColors)
  const decoratePendingEdge = decorateSequenceWithRenderInfo(PendingEdgeColors)
  const decorateVisitedBoundaryEvent = decorateBoundaryEventWithRenderInfo(
    VisitedBoundaryEventColors
  )
  const decoratePendingBoundaryEvent = decorateBoundaryEventWithRenderInfo(
    PendingBoundaryEventColors
  )

  return {
    nodes: [
      ..._differenceBy('id', visitedNodes, currentNodes).map(decorateVisitedNode),
      ...currentNodes.map(decorateCurrentNode),
      ...pendingNodes.map(decoratePendingNode),
    ],
    sequences: [...visitedEdges.map(decorateVisitedEdge), ...pendingEdges.map(decoratePendingEdge)],
    swimlanes,
    boundaryEvents: [
      ...visitedBoundaryEvents.map(decorateVisitedBoundaryEvent),
      ...pendingBoundaryEvents.map(decoratePendingBoundaryEvent),
    ],
  }
}
